import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Layouts
import PublicLayout from './components/PublicLayout';
import AdminLayout from './components/AdminLayout';
import PrivateRoute from './components/PrivateRoute';
import CaixaGuard from './components/CaixaGuard';

// Public Pages
import Home from './pages/public/Home';
import Cardapio from './pages/public/Cardapio';
import Localizacao from './pages/public/Localizacao';
import Contato from './pages/public/Contato';
import MesaCardapio from './pages/public/MesaCardapio';

// Admin Pages
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import PDV from './pages/admin/PDV';
import Pedidos from './pages/admin/Pedidos';
import CardapioAdmin from './pages/admin/CardapioAdmin';
import Mesas from './pages/admin/Mesas';
import Caixa from './pages/admin/Caixa';
import Estoque from './pages/admin/Estoque';
import Producao from './pages/admin/Producao';
import Comandas from './pages/admin/Comandas';
import Fiados from './pages/admin/Fiados';
import Clientes from './pages/admin/Clientes';
import Financeiro from './pages/admin/Financeiro';
import Usuarios from './pages/admin/Usuarios';
import Garcons from './pages/admin/Garcons';
import Relatorios from './pages/admin/Relatorios';
import Configuracoes from './pages/admin/Configuracoes';
import AtivosFixos from './pages/admin/AtivosFixos';

// Telas independentes
import Garcom from './pages/garcom/Garcom';
import KDS from './pages/kds/KDS';
import Display from './pages/display/Display';

const ADMIN = ['proprietario', 'administrador'];
const SOCIO_FUNC = ['funcionario', 'socio'];

// Protege rotas admin-only: redireciona funcionário/sócio para o PDV
function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user && SOCIO_FUNC.includes(user.perfil)) {
    return <Navigate to="/admin/pdv" replace />;
  }
  return children;
}

// Bloqueia apenas o perfil "funcionario" (sócio e admins têm acesso)
function SemFuncionarioRoute({ children }) {
  const { user } = useAuth();
  if (user && user.perfil === 'funcionario') {
    return <Navigate to="/admin/pdv" replace />;
  }
  return children;
}

// Rota index: admin vai ao Dashboard, funcionário/sócio vai ao PDV
function IndexRoute() {
  const { user } = useAuth();
  if (user && SOCIO_FUNC.includes(user.perfil)) {
    return <Navigate to="/admin/pdv" replace />;
  }
  return <Dashboard />;
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/cardapio" element={<Cardapio />} />
          <Route path="/localizacao" element={<Localizacao />} />
          <Route path="/contato" element={<Contato />} />
        </Route>

        {/* Cardápio QR Code por mesa (público, sem layout) */}
        <Route path="/mesa/:numero" element={<MesaCardapio />} />

        {/* Comanda Garçom (mobile, auth própria) */}
        <Route path="/garcom" element={<Garcom />} />

        {/* KDS - Monitor de Cozinha (fullscreen, auth própria) */}
        <Route path="/kds" element={<KDS />} />

        {/* Display Cliente (segundo monitor, público) */}
        <Route path="/display" element={<Display />} />

        {/* Login */}
        <Route path="/admin/login" element={<Login />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
          <Route index element={<IndexRoute />} />
          <Route path="pdv" element={<CaixaGuard><PDV /></CaixaGuard>} />
          <Route path="pedidos" element={<AdminRoute><Pedidos /></AdminRoute>} />
          <Route path="cardapio" element={<SemFuncionarioRoute><CardapioAdmin /></SemFuncionarioRoute>} />
          <Route path="mesas" element={<CaixaGuard><Mesas /></CaixaGuard>} />
          <Route path="caixa" element={<Caixa />} />
          <Route path="estoque" element={<SemFuncionarioRoute><Estoque /></SemFuncionarioRoute>} />
          <Route path="producao" element={<CaixaGuard><Producao /></CaixaGuard>} />
          <Route path="comandas" element={<CaixaGuard><Comandas /></CaixaGuard>} />
          <Route path="fiados" element={<SemFuncionarioRoute><Fiados /></SemFuncionarioRoute>} />
          <Route path="clientes" element={<SemFuncionarioRoute><Clientes /></SemFuncionarioRoute>} />
          <Route path="financeiro" element={<AdminRoute><Financeiro /></AdminRoute>} />
          <Route path="garcons" element={<AdminRoute><Garcons /></AdminRoute>} />
          <Route path="usuarios" element={<AdminRoute><Usuarios /></AdminRoute>} />
          <Route path="ativos" element={<AdminRoute><AtivosFixos /></AdminRoute>} />
          <Route path="relatorios" element={<AdminRoute><Relatorios /></AdminRoute>} />
          <Route path="configuracoes" element={<AdminRoute><Configuracoes /></AdminRoute>} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
